import { createStore as createIdbStore, get, set } from "idb-keyval";
import type { Middleware } from "@reduxjs/toolkit";
import type { RootState } from ".";
import type { OnboardingEntries } from "@features/onboarding/state/onboardingSlice";

const dbStore = createIdbStore("annogram-prefs", "kv");

const KEYS = {
  themeMode: "theme.mode",
  selectedOrgId: "view.selectedOrgId",
  workspaceId: "workspace.workspace_id",
  onboardingState: "onboarding.state",
} as const;

export interface PersistedPrefs {
  themeMode?: string;
  selectedOrgId?: string | null;
  workspaceId?: string;
  onboardingState?: OnboardingEntries;
}

let lastTheme: string | undefined;
let lastOrgId: string | null | undefined;
let lastWorkspaceId: string | undefined;
let lastOnboardingState: OnboardingEntries | undefined;

export const hydratePrefs = async (): Promise<PersistedPrefs> => {
  try {
    const [themeMode, selectedOrgId, workspaceId, onboardingState] =
      await Promise.all([
        get<string>(KEYS.themeMode, dbStore),
        get<string | null>(KEYS.selectedOrgId, dbStore),
        get<string>(KEYS.workspaceId, dbStore),
        get<OnboardingEntries>(KEYS.onboardingState, dbStore),
      ]);
    lastTheme = themeMode;
    lastOrgId = selectedOrgId;
    lastWorkspaceId = workspaceId;
    lastOnboardingState = onboardingState;
    return { themeMode, selectedOrgId, workspaceId, onboardingState };
  } catch {
    return {};
  }
};

export const persistencePrefsMiddleware: Middleware =
  (store) => (next) => (action) => {
    const result = next(action);
    const state = store.getState() as RootState;
    const theme = state.theme?.mode;
    const orgId = state.view?.selectedOrgId;
    const workspaceId = state.workspace?.workspace_id;
    if (theme !== lastTheme) {
      lastTheme = theme;
      set(KEYS.themeMode, theme, dbStore).catch(() => {});
    }
    if (orgId !== lastOrgId) {
      lastOrgId = orgId;
      set(KEYS.selectedOrgId, orgId, dbStore).catch(() => {});
    }
    if (workspaceId !== lastWorkspaceId && !state.view?.isDemoMode) {
      lastWorkspaceId = workspaceId;
      set(KEYS.workspaceId, workspaceId, dbStore).catch(() => {});
    }
    // Reference comparison suffices: the slice replaces the map object
    // on every change. This key is the demo/anonymous fallback — for
    // signed-in users the server copy is authoritative.
    const onboardingState = state.onboarding?.state;
    if (onboardingState !== lastOnboardingState) {
      lastOnboardingState = onboardingState;
      set(KEYS.onboardingState, onboardingState, dbStore).catch(() => {});
    }
    return result;
  };
